import { convertToUnicode, UnicodeStyle } from 'yay-text';

export const richTextTagPattern = /<\/?[a-z][\s\S]*>/i;
export const emptyQuillHtml = '<p><br></p>';

const richTextBlockTags = new Set([
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'P'
]);

const decodeHtmlEntities = (value: string) =>
  value
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const cleanRichTextPlainText = (value: string) =>
  value
    .replace(/\u00a0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

type RichTextStyle = {
  bold: boolean;
  italic: boolean;
};

type StyledTextSegment = {
  text: string;
  style: RichTextStyle;
};

const defaultRichTextStyle = { bold: false, italic: false };

const getUnicodeStyle = ({ bold, italic }: RichTextStyle) => {
  if (bold && italic) return UnicodeStyle.MATH_BOLD_ITALIC;
  if (bold) return UnicodeStyle.MATH_BOLD;
  if (italic) return UnicodeStyle.MATH_ITALIC;
  return null;
};

const applyUnicodeStyle = (text: string, style: RichTextStyle) => {
  const unicodeStyle = getUnicodeStyle(style);
  return unicodeStyle ? convertToUnicode(text, unicodeStyle) : text;
};

const stylesAreEqual = (left: RichTextStyle, right: RichTextStyle) =>
  left.bold === right.bold && left.italic === right.italic;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getUnicodeCharacterStyle = (character: string): StyledTextSegment => {
  const normalCharacter = convertToUnicode(character, UnicodeStyle.NORMAL);

  const styleCandidates: Array<{
    unicodeStyle: UnicodeStyle;
    style: RichTextStyle;
  }> = [
    {
      unicodeStyle: UnicodeStyle.MATH_BOLD_ITALIC,
      style: { bold: true, italic: true }
    },
    {
      unicodeStyle: UnicodeStyle.MATH_BOLD,
      style: { bold: true, italic: false }
    },
    {
      unicodeStyle: UnicodeStyle.MATH_ITALIC,
      style: { bold: false, italic: true }
    }
  ];

  for (const candidate of styleCandidates) {
    if (
      normalCharacter !== character &&
      convertToUnicode(normalCharacter, candidate.unicodeStyle) === character
    ) {
      return { text: normalCharacter, style: candidate.style };
    }
  }

  return { text: character, style: defaultRichTextStyle };
};

const getStyledTextSegments = (value: string) => {
  const characters = Array.from(value).map(getUnicodeCharacterStyle);

  return characters.map((character, index) => {
    if (
      character.text === ' ' &&
      stylesAreEqual(character.style, defaultRichTextStyle)
    ) {
      const previousStyle = characters[index - 1]?.style;
      const nextStyle = characters[index + 1]?.style;
      if (
        previousStyle &&
        nextStyle &&
        !stylesAreEqual(previousStyle, defaultRichTextStyle) &&
        stylesAreEqual(previousStyle, nextStyle)
      ) {
        return { ...character, style: previousStyle };
      }
    }

    return character;
  });
};

const textHasRichTextStyle = (segments: StyledTextSegment[]) =>
  segments.some((segment) => !stylesAreEqual(segment.style, defaultRichTextStyle));

const renderStyledSegment = (segment: StyledTextSegment) => {
  const text = escapeHtml(segment.text);
  if (segment.style.bold && segment.style.italic) {
    return `<strong><em>${text}</em></strong>`;
  }
  if (segment.style.bold) return `<strong>${text}</strong>`;
  if (segment.style.italic) return `<em>${text}</em>`;
  return text;
};

const renderStyledLine = (line: string) => {
  const renderedSegments = getStyledTextSegments(line).reduce<
    StyledTextSegment[]
  >((segments, segment) => {
    const previousSegment = segments[segments.length - 1];
    if (previousSegment && stylesAreEqual(previousSegment.style, segment.style)) {
      previousSegment.text += segment.text;
    } else {
      segments.push({ ...segment });
    }
    return segments;
  }, []);

  return renderedSegments.map(renderStyledSegment).join('');
};

const getTextFromRichTextNode = (
  node: Node,
  style: RichTextStyle = { bold: false, italic: false }
): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    return applyUnicodeStyle(text, style);
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const element = node as HTMLElement;
  const nextStyle = {
    bold: style.bold || ['B', 'STRONG'].includes(element.tagName),
    italic: style.italic || ['I', 'EM'].includes(element.tagName)
  };

  if (element.tagName === 'BR') return '\n';

  const text = Array.from(element.childNodes)
    .map((childNode) => getTextFromRichTextNode(childNode, nextStyle))
    .join('');

  if (!text || !richTextBlockTags.has(element.tagName)) return text;

  return text.endsWith('\n') ? text : `${text}\n`;
};

export const getCampaignPlainTextFromRichText = (value = '') => {
  if (value === emptyQuillHtml) return '';
  if (!value || !richTextTagPattern.test(value)) return value;

  const normalizedValue = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '</$1>\n');

  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = normalizedValue;

    return cleanRichTextPlainText(getTextFromRichTextNode(container));
  }

  const plainText = normalizedValue
    .replace(
      /<(strong|b)[^>]*>\s*<(em|i)[^>]*>(.*?)<\/\2>\s*<\/\1>/gis,
      (_match, _boldTag, _italicTag, content) =>
        applyUnicodeStyle(
          decodeHtmlEntities(content.replace(/<[^>]*>/g, '')),
          { bold: true, italic: true }
        )
    )
    .replace(
      /<(em|i)[^>]*>\s*<(strong|b)[^>]*>(.*?)<\/\2>\s*<\/\1>/gis,
      (_match, _italicTag, _boldTag, content) =>
        applyUnicodeStyle(
          decodeHtmlEntities(content.replace(/<[^>]*>/g, '')),
          { bold: true, italic: true }
        )
    )
    .replace(/<(strong|b)[^>]*>(.*?)<\/\1>/gis, (_match, _tag, content) =>
      applyUnicodeStyle(
        decodeHtmlEntities(content.replace(/<[^>]*>/g, '')),
        { bold: true, italic: false }
      )
    )
    .replace(/<(em|i)[^>]*>(.*?)<\/\1>/gis, (_match, _tag, content) =>
      applyUnicodeStyle(
        decodeHtmlEntities(content.replace(/<[^>]*>/g, '')),
        { bold: false, italic: true }
      )
    );

  return cleanRichTextPlainText(
    decodeHtmlEntities(plainText.replace(/<[^>]*>/g, ''))
  );
};

export const getCampaignRichTextFromPlainText = (value = '') => {
  if (!value || richTextTagPattern.test(value)) return value;

  const lines = value.split('\n');
  const segments = getStyledTextSegments(value);
  if (!textHasRichTextStyle(segments)) return value;

  return lines
    .map((line) => {
      const renderedLine = renderStyledLine(line);
      return renderedLine ? `<p>${renderedLine}</p>` : emptyQuillHtml;
    })
    .join('');
};
